import { Injectable } from '@angular/core';

/**
 * Persiste el journey_id (groupId del backend) en localStorage con TTL de 4h.
 * Esto permite correlacionar eventos entre cliente, picker y security,
 * y recuperar el QR si el cliente cierra la app durante la espera al picker.
 *
 * Todos los métodos son defensivos: nunca lanzan hacia afuera.
 */
@Injectable({ providedIn: 'root' })
export class JourneyService {

  private readonly JOURNEY_PREFIX = 'rt_journey';
  private readonly TTL_MS         = 4 * 60 * 60 * 1000; // 4 horas

  // Fallback en memoria para entornos donde localStorage no está disponible (iOS privado, etc.)
  private readonly memoryFallback = new Map<string, { id: string; ts: number }>();

  /** Persiste el groupId del backend como journey_id activo para la combinación tenant+store. */
  setJourneyId(tenantId: string, storeCode: string, groupId: string): void {
    this.write(`${this.JOURNEY_PREFIX}_${tenantId}_${storeCode}`, groupId);
  }

  /** Devuelve el journey_id activo, o null si no existe o expiró. */
  getJourneyId(tenantId: string, storeCode: string): string | null {
    return this.read(`${this.JOURNEY_PREFIX}_${tenantId}_${storeCode}`)?.id ?? null;
  }

  /** Limpia el journey al finalizar el retiro para no contaminar la próxima visita. */
  clearJourney(tenantId: string, storeCode: string): void {
    try { localStorage.removeItem(`${this.JOURNEY_PREFIX}_${tenantId}_${storeCode}`); } catch { /* ignorado */ }
    this.memoryFallback.delete(`${this.JOURNEY_PREFIX}_${tenantId}_${storeCode}`);
  }

  // ─── Helpers privados ───────────────────────────────────────────────────────

  private read(key: string): { id: string } | null {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return this.readMemory(key);
      const { id, ts } = JSON.parse(raw);
      if (Date.now() - ts > this.TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return { id };
    } catch {
      return this.readMemory(key);
    }
  }

  private readMemory(key: string): { id: string } | null {
    const entry = this.memoryFallback.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > this.TTL_MS) {
      this.memoryFallback.delete(key);
      return null;
    }
    return { id: entry.id };
  }

  private write(key: string, id: string): void {
    const value = { id, ts: Date.now() };
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      this.memoryFallback.set(key, value); // degradación silenciosa a in-memory
    }
  }
}
